import { vec3, vec2 } from "gl-matrix";
import { Vertex } from "@spi/Common/Geometry";
import { Ray, IIntersectableObject } from "@spi/Common/Math";

export class BoundingBox implements IIntersectableObject {

    private _vertices: Vertex[] = null;

    public min: vec3 | number[];
    public max: vec3 | number[];

    public get vertices(): Vertex[] {
        if (!this._vertices) {
            this._vertices = this.generateVertices();
        }
        return this._vertices;
    }

    public constructor(min: vec3 | number[], max: vec3 | number[]) {
        this.min = min;
        this.max = max;
    }

    private generateVertices(): Vertex[] {
        let min = this.min;
        let max = this.max;

        let coords = [
            vec3.fromValues(min[0], min[1], min[2]),
            vec3.fromValues(min[0], min[1], max[2]),
            vec3.fromValues(min[0], max[1], min[2]),
            vec3.fromValues(max[0], min[1], min[2]),
            vec3.fromValues(min[0], max[1], max[2]),
            vec3.fromValues(max[0], min[1], max[2]),
            vec3.fromValues(max[0], max[1], min[2]),
            vec3.fromValues(max[0], max[1], max[2])
        ];

        return coords.map(c => new Vertex().setPosition(c, true));
    }

    public intersectsRay(ray: Ray): boolean {
        return ray.intersectsBounds(this.min, this.max);
    }

    public canIntersectRay(): boolean {
        return true;
    }

    public isContained(bounds: { min: vec3 | number[], max: vec3 | number[] }): BoundingBoxContainment {

        let cMin = bounds.min;
        let cMax = bounds.max;

        let sMin = this.min;
        let sMax = this.max;

        if (cMax[0] < sMin[0]
            || cMin[0] > sMax[0]
            || cMax[1] < sMin[1]
            || cMin[1] > sMax[1]
            || cMax[2] < sMin[2]
            || cMin[2] > sMax[2]) {
            return BoundingBoxContainment.None; // no intersection at all
        }
        if (cMin[0] <= sMin[0]
            && cMax[0] >= sMax[0]
            && cMin[1] <= sMin[1]
            && cMax[1] >= sMax[1]
            && cMin[2] <= sMin[2]
            && cMax[2] >= sMax[2]) {
            return BoundingBoxContainment.Full; // fully contained
        }

        return BoundingBoxContainment.Partial; // Neither of the above, so intersection but not contained
    }

    public findAlignedEdge2D(bounds: { min: vec3 | number[], max: vec3 | number[] }): Edge2D {
        let thisEdges = this.getEdges2D(this);
        let boundEdges = this.getEdges2D(bounds);
        for (let thisEdge of thisEdges) {
            for (let boundEdge of boundEdges) {
                if (this.getAlignedAxis2D(thisEdge, boundEdge)) {

                    let edge = {
                        pointA: vec2.create(),
                        pointB: vec2.create()
                    };

                    for (let axis = 0; axis < 2; axis++) {
                        edge.pointA[axis] = Math.min(
                            Math.max(thisEdge.pointA[axis], thisEdge.pointB[axis]),
                            Math.max(boundEdge.pointA[axis], boundEdge.pointB[axis])
                        );

                        edge.pointB[axis] = Math.max(
                            Math.min(thisEdge.pointA[axis], thisEdge.pointB[axis]),
                            Math.min(boundEdge.pointA[axis], boundEdge.pointB[axis])
                        );
                    }
                    return edge;
                }
            }
        }

        return null;
    }

    private getAlignedAxis2D(a: Edge2D, b: Edge2D) {
        for (let axis = 0; axis < 2; axis++) {
            if (a.pointA[axis] == a.pointB[axis] && b.pointA[axis] == b.pointB[axis] && a.pointA[axis] == b.pointA[axis]) {
                return true;
            }
        }

        return false;
    }

    private getEdges2D(bounds: { min: vec3 | number[], max: vec3 | number[] }): Edge2D[] {

        let topLeft = vec2.fromValues(bounds.min[0], bounds.min[1]);
        let topRight = vec2.fromValues(bounds.max[0], bounds.min[1]);
        let bottomLeft = vec2.fromValues(bounds.min[0], bounds.max[1]);
        let bottomRight = vec2.fromValues(bounds.max[0], bounds.max[1]);

        return [
            { pointA: topLeft, pointB: topRight },
            { pointA: topLeft, pointB: bottomLeft },
            { pointA: topRight, pointB: bottomRight },
            { pointA: bottomLeft, pointB: bottomRight }
        ];
    }

    public getCenter(): vec3 {
        let center = vec3.subtract(vec3.create(), this.max, this.min);
        vec3.divide(center, center, [2, 2, 2]);

        vec3.add(center, this.min, center);

        return center;
    }
}

export type Edge2D = {
    pointA: vec2,
    pointB: vec2
}

export enum BoundingBoxContainment {
    None,
    Full,
    Partial
}


export interface IBoundingBoxObject extends IIntersectableObject {
    getBoundingBox(): BoundingBox;
}